<template>
	<AppLayout
		:title="trans('admin.users.rotations.requests.create.title')"
		:return-label="trans(unresolvedRequest ? 'menu.association.representative-rotations.history' : 'menu.association.representative-rotations.register')"
		:return-url="route(unresolvedRequest ? 'association.representative-rotations.history.index' : 'association.representative-rotations.index')"
	>
		<template #actions>
			<PrimaryButton v-if="!unresolvedRequest" :color="Color.Green" @click.stop="create">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('general.confirm') }}
			</PrimaryButton>
		</template>
		<Card v-if="unresolvedRequest" :title="`${dayjs(unresolvedRequest.election_begins_at).format(trans('general.dayjs.datetime'))} - ${dayjs(unresolvedRequest.election_ends_at).format(trans('general.dayjs.time'))}`" :subtitle="unresolvedRequest.college.name">
			<template #badges>
				<Badge :color="unresolvedRequest.colors" active>{{ trans(`requests.status.${unresolvedRequest.status}`) }}</Badge>
				<i v-if="unresolvedRequest.online" class="fa-solid fa-video-camera text-gray-400"/>
			</template>
			<template v-if="unresolvedRequest.needs_rescheduling">
				<LabeledObject full>
					<template #label>{{ trans('admin.users.rotations.requests.election_begins_at') }}</template>
					<IconDateTimeInput v-model="unresolvedRequest.election_begins_at" :min="dayjs().format(trans('general.dayjs.datetime'))" icon-class="fa-solid fa-hourglass-start"/>
				</LabeledObject>
				<LabeledObject full>
					<template #label>{{ trans('admin.users.rotations.requests.election_ends_at') }}</template>
					<IconDateTimeInput v-model="unresolvedRequest.election_ends_at" :min="dayjs().format(trans('general.dayjs.datetime'))" icon-class="fa-solid fa-hourglass-end"/>
				</LabeledObject>
			</template>
			<template v-if="unresolvedRequest.needs_rescheduling" #footer>
				<PrimaryButton :color="Color.Green" @click.stop="reschedule">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Card>
		<template v-else>
			<LabeledObject full>
				<template #label>{{ trans('admin.users.rotations.requests.online') }}</template>
				<Switch v-model="rotationRequest.online" label="admin.users.rotations.requests.online" full @click="toggle"/>
			</LabeledObject>
			<LabeledObject v-if="rotationRequest.online" full>
				<template #label>{{ trans('admin.users.rotations.requests.slot') }}</template>
				<p v-if="slots.length === 0">{{ trans('admin.users.rotations.requests.no_slots') }}</p>
				<CardGrid v-else>
					<Card v-for="(dateSlots, date) in slots" :title="dayjs(date).format(trans('general.dayjs.date'))">
						<List>
							<ListItem v-for="slot in dateSlots" :key="`period-slot-${slot.id}`" class="cursor-pointer" :title="`${dayjs(slot.begins_at).format(trans('general.dayjs.time'))} - ${dayjs(slot.ends_at).format(trans('general.dayjs.time'))}`" @click.stop="selectSlot(slot)">
								<i v-if="selectedSlot && slot.id === selectedSlot.id" class="fa-solid fa-circle-check text-green-400 dark:text-green-700"/>
								<i v-else class="fa-regular fa-circle text-gray-400"/>
							</ListItem>
						</List>
					</Card>
				</CardGrid>
			</LabeledObject>
			<div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-3">
				<LabeledObject full>
					<template #label>{{ trans('admin.users.rotations.requests.venue') }}</template>
					<input v-model="rotationRequest.venue" type="text">
				</LabeledObject>
				<LabeledObject full>
					<template #label>{{ trans('admin.users.rotations.requests.classroom') }}</template>
					<input v-model="rotationRequest.classroom" type="text">
				</LabeledObject>
				<LabeledObject full>
					<template #label>{{ trans('admin.users.rotations.requests.election_begins_at') }}</template>
					<input v-model="rotationRequest.election_begins_at" type="datetime-local" :min="min" :max="max">
				</LabeledObject>
				<LabeledObject full>
					<template #label>{{ trans('admin.users.rotations.requests.election_ends_at') }}</template>
					<input v-model="rotationRequest.election_ends_at" type="datetime-local" :min="min" :max="max">
				</LabeledObject>
			</div>
		</template>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {router} from '@inertiajs/vue3'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/Cards/Card.vue'
import {defineProps, type Ref, ref} from 'vue'
import List from '@/Components/Lists/List.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Badge from '@/Components/Badges/Badge.vue'
import Switch from '@/Components/Switch/Switch.vue'
import CardGrid from '@/Components/Cards/CardGrid.vue'
import ListItem from '@/Components/Lists/ListItem.vue'
import LabeledObject from '@/Components/LabeledObject.vue'
import ActionableHeader from '@/Components/ActionableHeader.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/helpers'
import {type RotationSlot} from '@/Types/Periods/Rotations/RotationSlot'
import IconDateTimeInput from '@/Components/Inputs/IconDateTimeInput.vue'
import {type RotationRequest} from '@/Types/Periods/Rotations/RotationRequest'

const props = defineProps<{
	min:string,
	max:string,
	slots:Array<RotationSlot>,
	unresolvedRequest:RotationRequest|null,
}>()

const working:Ref<boolean> = ref(false)
const slots:Ref<Array<RotationSlot>> = ref(props.slots)
const rotationRequest:Ref<RotationRequest> = ref({
	election_begins_at: null,
	election_ends_at: null,
	rotation_slot_id: null,
	online: 0,
	venue: '',
	classroom: '',
} as RotationRequest)
const selectedSlot:Ref<RotationSlot|null> = ref(null)
const unresolvedRequest:Ref<RotationRequest|null> = ref(props.unresolvedRequest)

function toggle():void{
	if(rotationRequest.value.online){
		rotationRequest.value.election_begins_at = null
		rotationRequest.value.election_ends_at = null
	} else {
		rotationRequest.value.slot_id = null
	}
}

function create():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('association.representative-rotations.requests.store'), rotationRequest.value).then(() => {
			showSuccess(trans('general.inserted'))
			router.get(route('association.representative-rotations.index'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function selectSlot(slot:RotationSlot):void{
	selectedSlot.value = slot
	rotationRequest.value.rotation_slot_id = slot.id
}

function reschedule():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representative-rotations.requests.update', unresolvedRequest.value.id), unresolvedRequest.value).then(({data}:{data:RotationRequest}) => {
			showSuccess(trans('general.updated'))
			unresolvedRequest.value = data
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>